import { type Layout } from 'react-grid-layout';
import { ILayoutConfig } from 'src/interfaces';
import { calculateOffset, parseLayoutConfig } from 'src/utils';

export const validateRotationSections = (
  layout: Layout[],
  templateLayout: Layout[],
): boolean => {
  // Create a Set to keep track of cells covered by the template
  const templateSet = new Set<string>();

  // Ensure at least one section has been created by the user
  if (layout.length === 0) {
    return false; // No user-drawn sections
  }

  // Mark all cells in the template layout as not filled (add to the set)
  for (const template of templateLayout) {
    for (let dx = 0; dx < template.w; dx++) {
      for (let dy = 0; dy < template.h; dy++) {
        const key = `${template.x + dx}-${template.y + dy}`;
        templateSet.add(key);
      }
    }
  }

  // Create a Set to keep track of cells covered by user-drawn sections
  const filledCells = new Set<string>();

  // Mark cells covered by user-drawn layout as filled (add to filledCells set)
  for (const section of layout) {
    for (let dx = 0; dx < section.w; dx++) {
      for (let dy = 0; dy < section.h; dy++) {
        const key = `${section.x + dx}-${section.y + dy}`;
        if (templateSet.has(key)) {
          filledCells.add(key);
        } else {
          // If a section goes beyond the template boundaries, the layout is invalid
          return false;
        }
      }
    }
  }

  // Check if all template sections crossed by user-drawn sections are fully covered
  for (const template of templateLayout) {
    let isCovered = true; // Assume the template is covered until proven otherwise

    for (let dx = 0; dx < template.w; dx++) {
      for (let dy = 0; dy < template.h; dy++) {
        const key = `${template.x + dx}-${template.y + dy}`;
        if (!filledCells.has(key)) {
          // If any cell in a template is not covered, mark it as not fully covered
          isCovered = false;
          break;
        }
      }
    }

    // If a template section is partially filled, check if it's crossed by any section
    if (!isCovered) {
      const sectionCoversTemplate = layout.some((section) => {
        return (
          section.x < template.x + template.w &&
          section.x + section.w > template.x &&
          section.y < template.y + template.h &&
          section.y + section.h > template.y
        );
      });

      if (sectionCoversTemplate) {
        return false; // Return false if a template section is partially filled
      }
    }
  }

  return true; // All template sections are properly filled or merged
};

const parseGridDimensions = (
  dimensions: string,
): { rows: number; cols: number } => {
  const [cols, rows] = dimensions
    .split('X')
    .map((num) => parseInt(num.trim(), 10));

  return { cols, rows };
};

export const calculateDimensionsFromGridChange = (
  rawDimension: string,
  screenConfigString: string | undefined,
) => {
  const { cols, rows } = parseGridDimensions(rawDimension);
  const { orientation, disclaimerPosition }: ILayoutConfig = parseLayoutConfig(
    screenConfigString ?? 'vertical3x3Grid',
  );

  const isVertical = orientation === 'vertical';
  const screenHeight = isVertical ? 1920 : 1080;
  const screenWidth = isVertical ? 1080 : 1920;
  const disclaimerHeightOriginal = isVertical
    ? screenHeight / 12
    : screenHeight / 15;
  const gridHeight = disclaimerPosition
    ? screenHeight - disclaimerHeightOriginal
    : screenHeight;
  const scale = 0.3;

  const gridSectionHeight = (gridHeight / rows) * scale;
  const gridSectionWidth = (screenWidth / cols) * scale;

  return {
    rows: rows,
    cols: cols,
    height: gridSectionHeight,
    width: gridSectionWidth,
  };
};

export const createRotationSectionPayload = (
  sections: any[],
  rotationScreenGridConfig: any,
  extraHeight: number,
) => {
  const scale = 0.3;

  return sections.map((section) => ({
    height: Math.round((section.h * rotationScreenGridConfig?.height) / scale),
    width: Math.round((section.w * rotationScreenGridConfig?.width) / scale),
    topOffset: calculateOffset(
      (section.y * rotationScreenGridConfig?.height) / scale,
      extraHeight,
    ),
    leftOffset: Math.round(
      (section.x * rotationScreenGridConfig?.width) / scale,
    ),
    cols: section.w,
    rows: section.h,
  }));
};
